import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { BrazilFlag, CatalanFlag, SpainFlag, UKFlag } from '@/components/custom/country-flags'
import { useTranslation } from '@/lib/locales'
import { Button } from '@/components/ui/button'
import { openCookiePreferences } from '@/lib/cookie-consent'
import { LINKEDIN_URL } from '@/lib/seo'
import { useLingui } from '@lingui/react/macro'
import { Link } from '@tanstack/react-router'
import { ChevronDownIcon } from 'lucide-react'
import type { ComponentType, SVGProps } from 'react'
import { cn } from '@/lib/utils'

const LOCALE_FLAGS: Record<string, ComponentType<SVGProps<SVGSVGElement>>> = {
  'es-ES': SpainFlag,
  'ca-ES': CatalanFlag,
  'en-US': UKFlag,
  'pt-BR': BrazilFlag,
}

/** The flag of `code`, or of the language in use when none is named. */
export function LocaleFlag(props: { code?: string; className?: string }) {
  const { language } = useTranslation()
  const Flag = LOCALE_FLAGS[props.code ?? language]
  return Flag ? <Flag aria-hidden className={cn('h-3.5 w-auto shrink-0 rounded-[2px]', props.className)} /> : null
}

/** A native select of the app's languages, so it works like the platform's own on every device. */
function LanguageSelect(props: { className?: string; label?: string }) {
  const { language, changeLanguage, languages } = useTranslation()
  return (
    <NativeSelect
      size="sm"
      aria-label={props.label}
      className={props.className}
      value={language}
      onChange={(event) => changeLanguage(event.target.value)}
    >
      {Object.entries(languages).map(([code, label]) => (
        <NativeSelectOption key={code} value={code}>
          {label}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  )
}

/** The language control for the landing footer, with the current locale's flag beside it. */
function FooterLanguageSelect(props: { className?: string }) {
  const { t } = useLingui()
  return (
    <label className={cn('flex items-center gap-2 p-3', props.className)}>
      <LocaleFlag />
      <span className="sr-only">{t`Select a Language`}</span>
      {/* Ghost: no border or fill, so it sits in the footer's line of plain text links, at their size. */}
      <LanguageSelect className="[&_select]:hover:bg-muted/50 [&_select]:border-transparent [&_select]:bg-transparent [&_select]:text-xs [&_select]:shadow-none dark:[&_select]:bg-transparent" />
    </label>
  )
}

/**
 * The same control, compact for a top bar: the flag and the language's two-letter code (ES, CA, EN, PT) with a chevron.
 * The real native select lies invisibly over it, so the list it opens still names each language in full and it works
 * with a keyboard and a screen reader like any select.
 */
export function CompactLanguageSelect(props: { className?: string }) {
  const { t } = useLingui()
  const { language } = useTranslation()
  return (
    <label
      className={cn(
        'hover:bg-muted has-[select:focus-visible]:ring-ring/50 relative flex h-9 cursor-pointer items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium transition-colors has-[select:focus-visible]:ring-3',
        props.className,
      )}
    >
      <LocaleFlag />
      <span aria-hidden>{language.slice(0, 2).toUpperCase()}</span>
      <ChevronDownIcon aria-hidden className="text-muted-foreground size-3.5" />
      <LanguageSelect
        label={t`Select a Language`}
        className="absolute inset-0 w-auto opacity-0 [&_select]:h-full [&_select]:cursor-pointer"
      />
    </label>
  )
}

/** LinkedIn's "in" alone, in the text colour (the brand's blue tile stays for sign-in). */
function LinkedInGlyph() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden className="size-5">
      <path
        fill="currentColor"
        transform="translate(2.2 2.2) scale(0.85)"
        d="M7.2 9.1H4.3v9.6h2.9zM5.75 4.5a1.68 1.68 0 1 0 0 3.36 1.68 1.68 0 0 0 0-3.36M18.7 18.7h-2.9v-4.66c0-1.11-.02-2.54-1.55-2.54-1.55 0-1.79 1.21-1.79 2.46v4.74H9.56V9.1h2.79v1.31h.04c.39-.74 1.34-1.51 2.76-1.51 2.95 0 3.5 1.94 3.5 4.47z"
      />
    </svg>
  )
}

// Read at module load, since a render may not read the clock: a server up across New Year shows last year until a deploy.
const year = new Date().getFullYear()

// `languageClassName` lets a page that also offers the language elsewhere (the landing's top bar) hide this one where it does.
export function Footer(props: { languageClassName?: string }) {
  const { t } = useLingui()

  return (
    <footer className="text-muted-foreground text-xs">
      <div className="mx-auto flex max-w-270 flex-wrap items-center justify-center p-4 md:justify-between">
        <div className="flex flex-wrap items-center">
          <FooterLanguageSelect className={props.languageClassName} />
          <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" title="LinkedIn" className="p-3">
            <LinkedInGlyph />
          </a>
          {/* The two passes read the year from their own clocks, which disagree for the hours either side of New
              Year everywhere but the server's timezone — not worth a hydration warning over a copyright notice. */}
          <span suppressHydrationWarning className="p-3">
            {t`© ${year} D'Anchiano. All rights reserved.`}
          </span>
        </div>

        {/* The push to the far end is the DESKTOP row's: on a phone the links wrap, and an `ml-auto` there
            shunts whichever one starts a line to the right, leaving the rows ragged. */}
        <Link to="/privacy-policy" className="p-3 hover:underline md:ml-auto">
          {t`Privacy Policy`}
        </Link>
        <Link to="/terms-of-use" className="p-3 hover:underline">{t`Terms of use`}</Link>
        <Link to="/cookies-policy" className="p-3 hover:underline">{t`Cookies Policy`}</Link>
        {/* The way back to a consent choice from anywhere, and the reason the banner can be kept off the
            policy pages where it would cover the text it asks about (see `useBannerFreeRoute`). A `Button`
            for the hydration gate: it dispatches an event only the client-only `CookieBanner` listens for,
            so a pre-hydration click has nothing to reach. Styled down to sit level with the links. */}
        <Button variant="link" className="p-3 text-xs hover:underline" onClick={openCookiePreferences}>
          {t`Cookie settings`}
        </Button>
      </div>
    </footer>
  )
}
